// Transaction check registry — THE single source of truth for the
// `transaction_checks` and `transactions_with_checks` views and for the
// frontend check metadata (CheckKey union, overridability, grouping).
//
// The SQL for both views is GENERATED from this file:
//   npm run gen:checks        → supabase/generated/transaction_checks.view.sql
// and applied by copying the generated statements into a migration.
// tests/architecture/checks-registry-sync.test.ts enforces that the registry,
// the generated artifact, and the live dump (supabase/schema/schema.sql) agree
// — the views must never be edited by hand.
//
// A standard check renders as the fixed tri-state skeleton:
//   CASE
//     [WHEN t.check_overrides ? '<key>' THEN 'completed']   -- when overridable
//     WHEN (NOT <required>)  THEN 'not_required'
//     WHEN <completed>       THEN 'completed'
//     ELSE 'pending'
//   END AS <key>_check
// Non-standard shapes (inverted pending_docs_review, override-only
// transport_invoices_issued) carry their verbatim CASE in `rawCase`.
//
// SQL fragments reference the fixed FROM-clause aliases:
//   t  = transactions            r  = transaction_rules (matched rule)
//   p  = products                tl = transport-leg aggregate (LATERAL)
//   ff = transaction_files aggregate (LATERAL)
//   ef = email_sends aggregate (LATERAL)
// Expressions are kept in pg_dump normalized form so the generated DDL is
// byte-identical to the dump after `./io db push dev` + `./io db dump dev`.
//
// Adding a check: add ONE entry here (+ i18n label + display position in
// transactionChecklist.ts CHECK_GROUPS), run `npm run gen:checks`, copy the
// generated DDL into a new migration, push + dump.

export type CheckGroupId = "transport" | "admin";

export interface StandardCheckDef {
  group: CheckGroupId;
  /** Manually overridable via transactions.check_overrides (CheckOverrideModal). */
  overridable: boolean;
  /** SQL boolean — when FALSE the check reads 'not_required'. */
  required: string;
  /** SQL boolean — when TRUE (and required) the check reads 'completed'. */
  completed: string;
  rawCase?: undefined;
}

export interface RawCheckDef {
  group: CheckGroupId;
  overridable: boolean;
  /** Verbatim select-list item (leading newline + CASE … END AS "<key>_check"). */
  rawCase: string;
  required?: undefined;
  completed?: undefined;
}

export type CheckDef = StandardCheckDef | RawCheckDef;

/** Object key order = column order in the `transaction_checks` view. */
export const CHECKS = {
  transport_complete: {
    group: "transport",
    overridable: true,
    required: `COALESCE("r"."transport_complete_required", true)`,
    completed: `((COALESCE("tl"."total", (0)::bigint) > 0) AND ("tl"."complete_count" = "tl"."total"))`,
  },
  transport_order_generated: {
    group: "transport",
    overridable: true,
    required: `(COALESCE("r"."transport_order_generated_required", true) AND (NOT ((COALESCE("tl"."total", (0)::bigint) = 0) OR (COALESCE("tl"."total", (0)::bigint) = COALESCE("tl"."spedition_count", (0)::bigint)))))`,
    completed: `COALESCE("ff"."transport_order_generated", false)`,
  },
  transport_order_sent: {
    group: "transport",
    overridable: true,
    required: `(COALESCE("r"."transport_order_sent_required", true) AND (NOT ((COALESCE("tl"."total", (0)::bigint) = 0) OR (COALESCE("tl"."total", (0)::bigint) = COALESCE("tl"."spedition_count", (0)::bigint)))))`,
    completed: `COALESCE("ef"."transport_order_sent", false)`,
  },
  loading_notification_sent: {
    group: "transport",
    overridable: true,
    required: `COALESCE("r"."loading_notification_sent_required", true)`,
    completed: `COALESCE("ef"."loading_notification_sent", false)`,
  },
  unloading_notification_sent: {
    group: "transport",
    overridable: true,
    required: `COALESCE("r"."unloading_notification_sent_required", true)`,
    completed: `COALESCE("ef"."unloading_notification_sent", false)`,
  },
  annex7_generated: {
    group: "admin",
    overridable: true,
    required: `(COALESCE("r"."annex7_generated_required", true) AND ("t"."border_type" <> 'domestic'::"text") AND ("p"."waste_code" IS NOT NULL) AND ("p"."waste_code" <> ''::"text"))`,
    completed: `COALESCE("ff"."annex7_generated", false)`,
  },
  cmr_generated: {
    group: "admin",
    overridable: true,
    required: `(COALESCE("r"."cmr_generated_required", true) AND ("t"."border_type" <> 'domestic'::"text") AND (COALESCE("tl"."truck_count", (0)::bigint) > 0))`,
    completed: `COALESCE("ff"."cmr_generated", false)`,
  },
  recycling_process_set: {
    group: "admin",
    overridable: true,
    required: `(COALESCE("r"."recycling_process_set_required", true) AND ("t"."border_type" <> 'domestic'::"text") AND ("p"."waste_code" IS NOT NULL) AND ("p"."waste_code" <> ''::"text"))`,
    completed: `("t"."recycling_process_code" IS NOT NULL)`,
  },
  loading_photo_approved: {
    group: "transport",
    overridable: true,
    required: `COALESCE("r"."loading_photo_approved_required", true)`,
    completed: `(("t"."unloaded_weight" IS NOT NULL) OR COALESCE("ff"."waste_image_approved", false))`,
  },
  annex_copy_approved: {
    group: "admin",
    overridable: true,
    required: `(COALESCE("r"."annex_copy_approved_required", true) AND ("t"."border_type" <> 'domestic'::"text"))`,
    completed: `COALESCE("ff"."annex7_copy_approved", false)`,
  },
  unload_weight_set: {
    group: "transport",
    overridable: true,
    required: `COALESCE("r"."unload_weight_set_required", true)`,
    completed: `("t"."unloaded_weight" IS NOT NULL)`,
  },
  number_of_bales_set: {
    group: "transport",
    overridable: true,
    required: `(COALESCE("r"."number_of_bales_set_required", true) AND ("t"."border_type" = 'non_eu'::"text"))`,
    completed: `("t"."number_of_bales" IS NOT NULL)`,
  },
  unloading_confirmation_sent: {
    group: "transport",
    overridable: true,
    required: `COALESCE("r"."unloading_confirmation_sent_required", true)`,
    completed: `COALESCE("ef"."unloading_confirmation_sent", false)`,
  },
  unload_price_set: {
    group: "admin",
    overridable: true,
    required: `COALESCE("r"."unload_price_set_required", true)`,
    completed: `("t"."unload_price" IS NOT NULL)`,
  },
  load_price_set: {
    group: "admin",
    overridable: true,
    required: `COALESCE("r"."load_price_set_required", true)`,
    completed: `("t"."load_price" IS NOT NULL)`,
  },
  annex_original_approved: {
    group: "admin",
    overridable: true,
    required: `(COALESCE("r"."annex_original_approved_required", true) AND ("t"."border_type" <> 'domestic'::"text"))`,
    completed: `COALESCE("ff"."annex7_original_approved", false)`,
  },
  cmr_original_approved: {
    group: "admin",
    overridable: true,
    required: `(COALESCE("r"."cmr_original_approved_required", true) AND ("t"."border_type" <> 'domestic'::"text") AND (COALESCE("tl"."truck_count", (0)::bigint) > 0))`,
    completed: `COALESCE("ff"."cmr_original_approved", false)`,
  },
  customs_docs_approved: {
    group: "admin",
    overridable: true,
    required: `(COALESCE("r"."customs_docs_approved_required", true) AND ("t"."border_type" = 'non_eu'::"text"))`,
    completed: `COALESCE("ff"."customs_docs_approved", false)`,
  },
  income_invoices_issued: {
    group: "admin",
    overridable: true,
    required: `COALESCE("r"."income_invoices_issued_required", true)`,
    completed: `((("t"."load_price" IS NULL) OR ("t"."load_price" <= (0)::numeric) OR ("t"."load_invoiced_status" = 'full'::"text")) AND (("t"."unload_price" IS NULL) OR ("t"."unload_price" <= (0)::numeric) OR ("t"."unload_invoiced_status" = 'full'::"text")))`,
  },
  cost_invoices_issued: {
    group: "admin",
    overridable: true,
    required: `COALESCE("r"."cost_invoices_issued_required", true)`,
    completed: `((("t"."load_price" IS NULL) OR ("t"."load_price" <= (0)::numeric) OR ("t"."load_received_invoiced_status" = 'full'::"text")) AND (("t"."unload_price" IS NULL) OR ("t"."unload_price" <= (0)::numeric) OR ("t"."unload_received_invoiced_status" = 'full'::"text")))`,
  },
  load_invoices_issued: {
    group: "admin",
    overridable: true,
    required: `COALESCE("r"."load_invoices_issued_required", true)`,
    completed: `(("t"."load_price" IS NULL) OR ("t"."load_price" <= (0)::numeric) OR ("t"."load_invoiced_status" = 'full'::"text"))`,
  },
  unload_invoices_issued: {
    group: "admin",
    overridable: true,
    required: `COALESCE("r"."unload_invoices_issued_required", true)`,
    completed: `(("t"."unload_price" IS NULL) OR ("t"."unload_price" <= (0)::numeric) OR ("t"."unload_invoiced_status" = 'full'::"text"))`,
  },
  transport_invoices_issued: {
    group: "admin",
    overridable: true,
    rawCase: `
        CASE
            WHEN ("t"."check_overrides" ? 'transport_invoices_issued'::"text") THEN 'completed'::"text"
            WHEN (NOT COALESCE("r"."transport_invoices_issued_required", true)) THEN 'not_required'::"text"
            ELSE 'pending'::"text"
        END AS "transport_invoices_issued_check"`,
  },
  pending_docs_review: {
    group: "admin",
    overridable: false,
    rawCase: `
        CASE
            WHEN COALESCE("ff"."has_pending_doc_review", false) THEN 'pending'::"text"
            ELSE 'completed'::"text"
        END AS "pending_docs_review_check"`,
  },
  unload_entry_request_sent: {
    group: "transport",
    overridable: true,
    required: `COALESCE("r"."unload_entry_request_sent_required", false)`,
    completed: `COALESCE("ef"."unload_entry_request_sent", false)`,
  },
  // The two copy checks below close the gap left by #288/#289: those issues
  // added the rule flags + the BRAKI/ODRZUCONE queue statuses but no tri-state
  // check, so the flags were unreachable from the checklist and from the
  // override modal. `required` mirrors REQ_COPY.{cmr,weight} (parenthesised —
  // here it is the operand of a NOT); `completed` is strict copy-approval,
  // exactly like annex_copy_approved: an approved ORIGINAL covers the copy
  // QUEUES (ORIGINAL_COVERS), it does not complete the copy CHECK.
  // Both are intra-EU only (#305): a transport leaving the EU is verified on
  // its Aneks VII alone, so neither copy is required on `non_eu`.
  // Their columns are TAIL columns — see CHECKS_TAIL_KEYS.
  cmr_copy_approved: {
    group: "admin",
    overridable: true,
    required: `(COALESCE("r"."cmr_copy_approved_required", true) AND ("t"."border_type" = 'eu'::"text") AND (COALESCE("tl"."truck_count", (0)::bigint) > 0))`,
    completed: `COALESCE("ff"."cmr_copy_approved", false)`,
  },
  weight_ticket_copy_approved: {
    group: "admin",
    overridable: true,
    required: `(COALESCE("r"."weight_ticket_copy_approved_required", true) AND ("t"."border_type" = 'eu'::"text"))`,
    completed: `COALESCE("ff"."weight_ticket_copy_approved", false)`,
  },
} as const satisfies Record<string, CheckDef>;

export type CheckKey = keyof typeof CHECKS;

export const ALL_CHECK_KEYS_ORDERED = Object.keys(CHECKS) as CheckKey[];

export const NON_OVERRIDABLE_CHECK_KEYS = ALL_CHECK_KEYS_ORDERED.filter(
  (k) => !(CHECKS[k] as CheckDef).overridable,
);

/** Checks whose view column sits AFTER `CHECKS_EXTRA_STATUS_ITEMS`.
 *
 *  `transaction_checks` is only ever CREATE OR REPLACE'd — `transactions_edpr_view`
 *  and `transactions_with_checks` depend on it, and Postgres allows APPENDING
 *  columns to a replaced view but never reordering them. A check added after
 *  the status columns already shipped therefore renders last, not in key order.
 *  Everything else (the CheckKey union, FE grouping, the WITH_CHECKS ledger)
 *  still reads registry key order — this list ONLY moves select-list position. */
export const CHECKS_TAIL_KEYS: CheckKey[] = ["cmr_copy_approved", "weight_ticket_copy_approved"];

/** Select-list order of the tri-state columns: key order, minus the tail. */
export const CHECKS_LEADING_KEYS: CheckKey[] = ALL_CHECK_KEYS_ORDERED.filter(
  (k) => !CHECKS_TAIL_KEYS.includes(k),
);

// ─── View plumbing (verbatim pg_dump form) ───────────────────────────────

// ── Document-copy coverage (issues #288 / #289) ──────────────────────────
//
// The logistics "Kopie dok." (BRAKI) and "Odrzucone" tabs are defined over
// THREE required document types — Aneks VII, CMR and kwit wagowy — not over
// the annex alone. Per type, requiredness first (each gated by its own
// `transaction_rules` flag), then coverage:
//
//   copy uploaded   — a copy of that type exists, WHATEVER its review status
//                     (#288: "Status poprawności wgranej kopii nie decyduje
//                     o obecności IRM w tym zadaniu"), OR the type's copy check
//                     carries a manual override (handled outside the system —
//                     all three types, symmetrically).
//   original covers — an original of the same type exists and is not rejected,
//                     i.e. approved or still awaiting review (#288 example
//                     table, case 4 vs case 5).
//
// `copies_missing_status`  = 'missing'  when a required type has neither.
// `copies_rejected_status` = 'rejected' when a required type's copy is rejected
//                            (and not also approved) and no original of that
//                            same type covers it. Rejected ORIGINALS are not
//                            this tab's business (#289 ruling).
//
// Transport leaving the EU (#305, `border_type = 'non_eu'`): only Aneks VII
// counts — CMR and kwit wagowy are required on intra-EU transports alone. On
// such a transport ANY Aneks VII original on file, even a rejected one, closes
// the BRAKI gap (MISSING_ORIGINAL_COVERS); the Odrzucone column keeps #289's
// rule that a rejected original never cancels a rejected copy.
//
// Fragments are kept in pg_dump normalized form, like every other expression
// in this file; only the CASE skeleton is assembled in TS so the three
// document types cannot drift apart.
// Unparenthesised on purpose: Postgres flattens the LEFT operand of an AND
// chain into its parent, so `(REQ AND x)` round-trips through pg_dump as
// `reqA AND reqB AND x`. Wrapped explicitly wherever it is not the left
// operand (i.e. under NOT).
const REQ_COPY = {
  annex7: `COALESCE("r"."annex_copy_approved_required", true) AND ("t"."border_type" <> 'domestic'::"text")`,
  cmr: `COALESCE("r"."cmr_copy_approved_required", true) AND ("t"."border_type" = 'eu'::"text") AND (COALESCE("tl"."truck_count", (0)::bigint) > 0)`,
  weight: `COALESCE("r"."weight_ticket_copy_approved_required", true) AND ("t"."border_type" = 'eu'::"text")`,
} as const;

// Unparenthesised for the same reason as REQ_COPY — always the left operand of
// the coverage OR, so Postgres folds it into the parent.
const COPY_UPLOADED = {
  annex7: `COALESCE("ff"."annex7_copy_uploaded", false) OR ("t"."check_overrides" ? 'annex_copy_approved'::"text")`,
  cmr: `COALESCE("ff"."cmr_copy_uploaded", false) OR ("t"."check_overrides" ? 'cmr_copy_approved'::"text")`,
  weight: `COALESCE("ff"."weight_ticket_copy_uploaded", false) OR ("t"."check_overrides" ? 'weight_ticket_copy_approved'::"text")`,
} as const;

const COPY_REJECTED = {
  annex7: `(COALESCE("ff"."annex7_copy_rejected", false) AND (NOT COALESCE("ff"."annex7_copy_approved", false)) AND (NOT ("t"."check_overrides" ? 'annex_copy_approved'::"text")))`,
  cmr: `(COALESCE("ff"."cmr_copy_rejected", false) AND (NOT COALESCE("ff"."cmr_copy_approved", false)) AND (NOT ("t"."check_overrides" ? 'cmr_copy_approved'::"text")))`,
  weight: `(COALESCE("ff"."weight_ticket_copy_rejected", false) AND (NOT COALESCE("ff"."weight_ticket_copy_approved", false)) AND (NOT ("t"."check_overrides" ? 'weight_ticket_copy_approved'::"text")))`,
} as const;

const ORIGINAL_COVERS = {
  annex7: `(COALESCE("ff"."annex7_original_approved", false) OR ("t"."check_overrides" ? 'annex_original_approved'::"text") OR (COALESCE("ff"."annex7_original_uploaded", false) AND (NOT COALESCE("ff"."annex7_original_rejected", false))))`,
  cmr: `(COALESCE("ff"."cmr_original_approved", false) OR ("t"."check_overrides" ? 'cmr_original_approved'::"text") OR (COALESCE("ff"."cmr_original_uploaded", false) AND (NOT COALESCE("ff"."cmr_original_rejected", false))))`,
  weight: `(COALESCE("ff"."weight_ticket_original_approved", false) OR (COALESCE("ff"."weight_ticket_original_uploaded", false) AND (NOT COALESCE("ff"."weight_ticket_original_rejected", false))))`,
} as const;

// BRAKI-only variant of ORIGINAL_COVERS: on a non-EU transport an uploaded
// Aneks VII original covers the missing copy whatever its review status (#305).
const MISSING_ORIGINAL_COVERS = {
  annex7: `(COALESCE("ff"."annex7_original_approved", false) OR ("t"."check_overrides" ? 'annex_original_approved'::"text") OR (COALESCE("ff"."annex7_original_uploaded", false) AND ((NOT COALESCE("ff"."annex7_original_rejected", false)) OR ("t"."border_type" = 'non_eu'::"text"))))`,
  cmr: ORIGINAL_COVERS.cmr,
  weight: ORIGINAL_COVERS.weight,
} as const;

const COPY_DOC_KEYS = ["annex7", "cmr", "weight"] as const;

const NONE_COPY_REQUIRED = COPY_DOC_KEYS.map((k) => `(NOT (${REQ_COPY[k]}))`).join(" AND ");

const ANY_COPY_MISSING = COPY_DOC_KEYS
  .map((k) => `(${REQ_COPY[k]} AND (NOT (${COPY_UPLOADED[k]} OR ${MISSING_ORIGINAL_COVERS[k]})))`)
  .join(" OR ");

const ANY_COPY_REJECTED = COPY_DOC_KEYS
  .map((k) => `(${REQ_COPY[k]} AND ${COPY_REJECTED[k]} AND (NOT ${ORIGINAL_COVERS[k]}))`)
  .join(" OR ");

/** `not_required` / `<hit>` / `<miss>` ladder shared by both copy-doc columns. */
function copyDocsStatusItem(
  predicate: string, hit: string, miss: string, alias: string,
): string {
  return [
    "",
    "        CASE",
    `            WHEN (${NONE_COPY_REQUIRED}) THEN 'not_required'::"text"`,
    `            WHEN (${predicate}) THEN '${hit}'::"text"`,
    `            ELSE '${miss}'::"text"`,
    `        END AS "${alias}"`,
  ].join("\n");
}

/** transaction_checks: richer status columns appended after the tri-states. */
export const CHECKS_EXTRA_STATUS_ITEMS: string[] = [
  "\n        CASE\n            WHEN (\"t\".\"check_overrides\" ? 'annex_original_approved'::\"text\") THEN 'completed'::\"text\"\n            WHEN (NOT (COALESCE(\"r\".\"annex_original_approved_required\", true) AND (\"t\".\"border_type\" <> 'domestic'::\"text\"))) THEN 'not_required'::\"text\"\n            WHEN COALESCE(\"ff\".\"annex7_original_approved\", false) THEN 'completed'::\"text\"\n            WHEN COALESCE(\"ff\".\"annex7_original_rejected\", false) THEN 'rejected'::\"text\"\n            WHEN COALESCE(\"ff\".\"annex7_original_pending\", false) THEN 'pending_review'::\"text\"\n            WHEN COALESCE(\"ff\".\"annex7_original_uploaded\", false) THEN 'pending_review'::\"text\"\n            ELSE 'missing'::\"text\"\n        END AS \"annex_original_status\"",
  "\n        CASE\n            WHEN (\"t\".\"check_overrides\" ? 'cmr_original_approved'::\"text\") THEN 'completed'::\"text\"\n            WHEN (NOT (COALESCE(\"r\".\"cmr_original_approved_required\", true) AND (\"t\".\"border_type\" <> 'domestic'::\"text\") AND (COALESCE(\"tl\".\"truck_count\", (0)::bigint) > 0))) THEN 'not_required'::\"text\"\n            WHEN COALESCE(\"ff\".\"cmr_original_approved\", false) THEN 'completed'::\"text\"\n            WHEN COALESCE(\"ff\".\"cmr_original_rejected\", false) THEN 'rejected'::\"text\"\n            WHEN COALESCE(\"ff\".\"cmr_original_pending\", false) THEN 'pending_review'::\"text\"\n            WHEN COALESCE(\"ff\".\"cmr_original_uploaded\", false) THEN 'pending_review'::\"text\"\n            ELSE 'missing'::\"text\"\n        END AS \"cmr_original_status\"",
  "\n        CASE\n            WHEN ((NOT (COALESCE(\"r\".\"annex_original_approved_required\", true) AND (\"t\".\"border_type\" <> 'domestic'::\"text\"))) AND (NOT (COALESCE(\"r\".\"cmr_original_approved_required\", true) AND (\"t\".\"border_type\" <> 'domestic'::\"text\") AND (COALESCE(\"tl\".\"truck_count\", (0)::bigint) > 0)))) THEN 'not_required'::\"text\"\n            WHEN (((NOT (COALESCE(\"r\".\"annex_original_approved_required\", true) AND (\"t\".\"border_type\" <> 'domestic'::\"text\"))) OR (COALESCE(\"ff\".\"annex7_original_approved\", false) OR (\"t\".\"check_overrides\" ? 'annex_original_approved'::\"text\"))) AND ((NOT (COALESCE(\"r\".\"cmr_original_approved_required\", true) AND (\"t\".\"border_type\" <> 'domestic'::\"text\") AND (COALESCE(\"tl\".\"truck_count\", (0)::bigint) > 0))) OR (COALESCE(\"ff\".\"cmr_original_approved\", false) OR (\"t\".\"check_overrides\" ? 'cmr_original_approved'::\"text\")))) THEN 'completed'::\"text\"\n            WHEN (COALESCE(\"ff\".\"annex7_original_uploaded\", false) OR COALESCE(\"ff\".\"cmr_original_uploaded\", false)) THEN 'action_needed'::\"text\"\n            ELSE 'awaiting_upload'::\"text\"\n        END AS \"original_docs_status\"",
  "\n        CASE\n            WHEN (\"t\".\"check_overrides\" ? 'annex_copy_approved'::\"text\") THEN 'completed'::\"text\"\n            WHEN (NOT (COALESCE(\"r\".\"annex_copy_approved_required\", true) AND (\"t\".\"border_type\" <> 'domestic'::\"text\"))) THEN 'not_required'::\"text\"\n            WHEN COALESCE(\"ff\".\"annex7_copy_approved\", false) THEN 'completed'::\"text\"\n            WHEN COALESCE(\"ff\".\"annex7_copy_rejected\", false) THEN 'rejected'::\"text\"\n            WHEN COALESCE(\"ff\".\"annex7_copy_pending\", false) THEN 'pending_review'::\"text\"\n            WHEN COALESCE(\"ff\".\"annex7_copy_uploaded\", false) THEN 'pending_review'::\"text\"\n            ELSE 'missing'::\"text\"\n        END AS \"annex_copy_status\"",
  copyDocsStatusItem(ANY_COPY_MISSING, "missing", "complete", "copies_missing_status"),
  copyDocsStatusItem(ANY_COPY_REJECTED, "rejected", "clear", "copies_rejected_status"),
];

export const CHECKS_FROM_SQL = `   FROM ((((("public"."transactions" "t"
     LEFT JOIN "public"."transaction_rules" "r" ON (("r"."id" = "t"."rule_id")))
     LEFT JOIN "public"."products" "p" ON (("p"."id" = "t"."product_id")))
     LEFT JOIN LATERAL ( SELECT "count"(*) FILTER (WHERE (NOT "tt"."is_deleted")) AS "total",
            "count"(*) FILTER (WHERE ((NOT "tt"."is_deleted") AND ("tt"."transport_type" = 'spedition'::"text"))) AS "spedition_count",
            "count"(*) FILTER (WHERE ((NOT "tt"."is_deleted") AND ("tt"."transport_type" = 'truck'::"text"))) AS "truck_count",
            "count"(*) FILTER (WHERE ((NOT "tt"."is_deleted") AND ((("tt"."transport_type" = 'truck'::"text") AND ("tt"."transport_company_id" IS NOT NULL)) OR (("tt"."transport_type" = ANY (ARRAY['train'::"text", 'ship'::"text"])) AND ("tt"."transport_company_id" IS NOT NULL) AND ("tt"."container_number" IS NOT NULL)) OR (("tt"."transport_type" = 'spedition'::"text") AND ("tt"."transport_company_id" IS NOT NULL))))) AS "complete_count"
           FROM "public"."transaction_transports" "tt"
          WHERE ("tt"."transaction_id" = "t"."id")) "tl" ON (true))
     LEFT JOIN LATERAL ( SELECT "bool_or"((("tf"."document_type" = 'transport_order'::"text") AND ("tf"."document_status" = 'generated'::"text"))) AS "transport_order_generated",
            "bool_or"((("tf"."document_type" = 'annex7'::"text") AND ("tf"."document_status" = 'generated'::"text"))) AS "annex7_generated",
            "bool_or"((("tf"."document_type" = 'cmr'::"text") AND ("tf"."document_status" = 'generated'::"text"))) AS "cmr_generated",
            "bool_or"((("tf"."document_type" = 'waste_image'::"text") AND ("tf"."review_status" = 'approved'::"text"))) AS "waste_image_approved",
            "bool_or"((("tf"."document_type" = 'annex7'::"text") AND ("tf"."document_status" = 'copy'::"text") AND ("tf"."review_status" = 'approved'::"text"))) AS "annex7_copy_approved",
            "bool_or"((("tf"."document_type" = 'annex7'::"text") AND ("tf"."document_status" = 'original'::"text") AND ("tf"."review_status" = 'approved'::"text"))) AS "annex7_original_approved",
            "bool_or"((("tf"."document_type" = 'cmr'::"text") AND ("tf"."document_status" = 'original'::"text") AND ("tf"."review_status" = 'approved'::"text"))) AS "cmr_original_approved",
            "bool_or"((("tf"."document_type" = ANY (ARRAY['ie529'::"text", 'ie599'::"text"])) AND ("tf"."review_status" = 'approved'::"text"))) AS "customs_docs_approved",
            "bool_or"((("tf"."review_status" = 'pending'::"text") AND ("tf"."document_status" = ANY (ARRAY['copy'::"text", 'original'::"text"])) AND ("tf"."document_type" <> 'waste_image'::"text"))) AS "has_pending_doc_review",
            "bool_or"((("tf"."document_type" = 'annex7'::"text") AND ("tf"."document_status" = 'original'::"text"))) AS "annex7_original_uploaded",
            "bool_or"((("tf"."document_type" = 'annex7'::"text") AND ("tf"."document_status" = 'original'::"text") AND ("tf"."review_status" = 'pending'::"text"))) AS "annex7_original_pending",
            "bool_or"((("tf"."document_type" = 'annex7'::"text") AND ("tf"."document_status" = 'original'::"text") AND ("tf"."review_status" = 'not_approved'::"text"))) AS "annex7_original_rejected",
            "bool_or"((("tf"."document_type" = 'cmr'::"text") AND ("tf"."document_status" = 'original'::"text"))) AS "cmr_original_uploaded",
            "bool_or"((("tf"."document_type" = 'cmr'::"text") AND ("tf"."document_status" = 'original'::"text") AND ("tf"."review_status" = 'pending'::"text"))) AS "cmr_original_pending",
            "bool_or"((("tf"."document_type" = 'cmr'::"text") AND ("tf"."document_status" = 'original'::"text") AND ("tf"."review_status" = 'not_approved'::"text"))) AS "cmr_original_rejected",
            "bool_or"((("tf"."document_type" = 'annex7'::"text") AND ("tf"."document_status" = 'copy'::"text"))) AS "annex7_copy_uploaded",
            "bool_or"((("tf"."document_type" = 'annex7'::"text") AND ("tf"."document_status" = 'copy'::"text") AND ("tf"."review_status" = 'pending'::"text"))) AS "annex7_copy_pending",
            "bool_or"((("tf"."document_type" = 'annex7'::"text") AND ("tf"."document_status" = 'copy'::"text") AND ("tf"."review_status" = 'not_approved'::"text"))) AS "annex7_copy_rejected",
            "bool_or"((("tf"."document_type" = 'cmr'::"text") AND ("tf"."document_status" = 'copy'::"text"))) AS "cmr_copy_uploaded",
            "bool_or"((("tf"."document_type" = 'cmr'::"text") AND ("tf"."document_status" = 'copy'::"text") AND ("tf"."review_status" = 'approved'::"text"))) AS "cmr_copy_approved",
            "bool_or"((("tf"."document_type" = 'cmr'::"text") AND ("tf"."document_status" = 'copy'::"text") AND ("tf"."review_status" = 'not_approved'::"text"))) AS "cmr_copy_rejected",
            "bool_or"((("tf"."document_type" = 'weight_tickets'::"text") AND ("tf"."document_status" = 'copy'::"text"))) AS "weight_ticket_copy_uploaded",
            "bool_or"((("tf"."document_type" = 'weight_tickets'::"text") AND ("tf"."document_status" = 'copy'::"text") AND ("tf"."review_status" = 'approved'::"text"))) AS "weight_ticket_copy_approved",
            "bool_or"((("tf"."document_type" = 'weight_tickets'::"text") AND ("tf"."document_status" = 'copy'::"text") AND ("tf"."review_status" = 'not_approved'::"text"))) AS "weight_ticket_copy_rejected",
            "bool_or"((("tf"."document_type" = 'weight_tickets'::"text") AND ("tf"."document_status" = 'original'::"text"))) AS "weight_ticket_original_uploaded",
            "bool_or"((("tf"."document_type" = 'weight_tickets'::"text") AND ("tf"."document_status" = 'original'::"text") AND ("tf"."review_status" = 'approved'::"text"))) AS "weight_ticket_original_approved",
            "bool_or"((("tf"."document_type" = 'weight_tickets'::"text") AND ("tf"."document_status" = 'original'::"text") AND ("tf"."review_status" = 'not_approved'::"text"))) AS "weight_ticket_original_rejected"
           FROM "public"."transaction_files" "tf"
          WHERE (("tf"."transaction_id" = "t"."id") AND ("tf"."is_latest" = true))) "ff" ON (true))
     LEFT JOIN LATERAL ( SELECT "bool_or"((("es"."email_type" = 'transport_order'::"text") AND ("es"."sent_at" IS NOT NULL))) AS "transport_order_sent",
            "bool_or"((("es"."email_type" = 'loading_notification'::"text") AND ("es"."sent_at" IS NOT NULL))) AS "loading_notification_sent",
            "bool_or"((("es"."email_type" = 'unloading_notification'::"text") AND ("es"."sent_at" IS NOT NULL))) AS "unloading_notification_sent",
            "bool_or"((("es"."email_type" = 'unloading_confirmation'::"text") AND ("es"."sent_at" IS NOT NULL))) AS "unloading_confirmation_sent",
            "bool_or"((("es"."email_type" = 'unload_entry_request'::"text") AND ("es"."sent_at" IS NOT NULL))) AS "unload_entry_request_sent"
           FROM "public"."email_sends" "es"
          WHERE ("es"."transaction_id" = "t"."id")) "ef" ON (true))`;

/** transactions_with_checks select-list ledger — order IS the column order.
 *  Removing or reordering an entry means the migration must DROP + recreate
 *  the view (CREATE OR REPLACE only allows appending). */
export type WithChecksItem = { src: "t" | "tc" | "tf"; col: string; raw?: undefined } | { raw: string; src?: undefined; col?: undefined };

export const WITH_CHECKS_ITEMS: WithChecksItem[] = [
  { src: "t", col: "id" },
  { src: "t", col: "external_id" },
  { src: "t", col: "load_date" },
  { src: "t", col: "load_time" },
  { src: "t", col: "reload_date" },
  { src: "t", col: "unload_date" },
  { src: "t", col: "unload_time" },
  { src: "t", col: "loaded_weight" },
  { src: "t", col: "unloaded_weight" },
  { src: "t", col: "number_of_bales" },
  { src: "t", col: "product_id" },
  { src: "t", col: "load_place_company_id" },
  { src: "t", col: "unload_place_company_id" },
  { src: "t", col: "load_commercial_company_id" },
  { src: "t", col: "unload_commercial_company_id" },
  { src: "t", col: "exporter_company_id" },
  { src: "t", col: "importer_company_id" },
  { src: "t", col: "load_price" },
  { src: "t", col: "load_price_currency" },
  { src: "t", col: "load_price_type" },
  { src: "t", col: "load_price_incoterms" },
  { src: "t", col: "unload_price" },
  { src: "t", col: "unload_price_currency" },
  { src: "t", col: "unload_price_type" },
  { src: "t", col: "unload_price_incoterms" },
  { src: "t", col: "gd_status" },
  { src: "t", col: "request_dpr" },
  { src: "t", col: "issue_dpr" },
  { src: "t", col: "is_deleted" },
  { src: "t", col: "created_at" },
  { src: "t", col: "updated_at" },
  { src: "t", col: "reference_number" },
  { src: "t", col: "transit" },
  { src: "t", col: "load_price_list_id" },
  { src: "t", col: "unload_price_list_id" },
  { src: "t", col: "pickup_date_from" },
  { src: "t", col: "pickup_date_to" },
  { src: "t", col: "border_type" },
  { src: "t", col: "logistics_employee_id" },
  { src: "t", col: "load_sales_employee_id" },
  { src: "t", col: "unload_sales_employee_id" },
  { src: "t", col: "status_override" },
  { src: "t", col: "load_price_vat_rate_code" },
  { src: "t", col: "unload_price_vat_rate_code" },
  { src: "t", col: "rule_id" },
  { src: "t", col: "search_vector" },
  { src: "t", col: "import_drive_folder_id" },
  { src: "t", col: "recycling_process_code" },
  { src: "t", col: "load_invoiced_status" },
  { src: "t", col: "unload_invoiced_status" },
  { src: "t", col: "load_received_invoiced_status" },
  { src: "t", col: "unload_received_invoiced_status" },
  { src: "tc", col: "transport_complete_check" },
  { src: "tc", col: "transport_order_generated_check" },
  { src: "tc", col: "transport_order_sent_check" },
  { src: "tc", col: "loading_notification_sent_check" },
  { src: "tc", col: "unloading_notification_sent_check" },
  { src: "tc", col: "unload_entry_request_sent_check" },
  { src: "tc", col: "annex7_generated_check" },
  { src: "tc", col: "cmr_generated_check" },
  { src: "tc", col: "recycling_process_set_check" },
  { src: "tc", col: "loading_photo_approved_check" },
  { src: "tc", col: "annex_copy_approved_check" },
  { src: "tc", col: "unload_weight_set_check" },
  { src: "tc", col: "number_of_bales_set_check" },
  { src: "tc", col: "unloading_confirmation_sent_check" },
  { src: "tc", col: "unload_price_set_check" },
  { src: "tc", col: "load_price_set_check" },
  { src: "tc", col: "annex_original_approved_check" },
  { src: "tc", col: "cmr_original_approved_check" },
  { src: "tc", col: "customs_docs_approved_check" },
  { src: "tc", col: "income_invoices_issued_check" },
  { src: "tc", col: "cost_invoices_issued_check" },
  { src: "tc", col: "load_invoices_issued_check" },
  { src: "tc", col: "unload_invoices_issued_check" },
  { src: "tc", col: "transport_invoices_issued_check" },
  { src: "tc", col: "pending_docs_review_check" },
  { src: "t", col: "order_number" },
  { raw: `
    ("t"."border_type" = 'domestic'::"text") AS "annex_na"` },
  { raw: `
    ("tc"."transport_order_generated_check" = 'not_required'::"text") AS "transport_order_na"` },
  { raw: `
        CASE
            WHEN ("tc"."pending_docs_review_check" = 'completed'::"text") THEN 'complete'::"text"
            WHEN ("tc"."pending_docs_review_check" = 'pending'::"text") THEN 'partial'::"text"
            ELSE 'none'::"text"
        END AS "docs_review_status"` },
  { raw: `
    (EXISTS ( SELECT 1
           FROM "public"."email_sends" "es"
          WHERE (("es"."transaction_id" = "t"."id") AND ("es"."email_type" = ANY (ARRAY['documents_reminder'::"text", 'documents_scan_reminder'::"text"])) AND ("es"."sent_at" IS NOT NULL) AND ((("es"."sent_at" AT TIME ZONE 'UTC'::"text"))::"date" = ((CURRENT_TIMESTAMP AT TIME ZONE 'UTC'::"text"))::"date")))) AS "documents_reminder_sent_today"` },
  { src: "tf", col: "margin_pln" },
  { src: "tf", col: "transport_pln" },
  { src: "tf", col: "total_cost_pln" },
  { src: "tf", col: "total_income_pln" },
  { src: "tf", col: "load_net_pln" },
  { src: "tf", col: "unload_net_pln" },
  { src: "tf", col: "edpr_income_pln" },
  { src: "tf", col: "load_fx_rate" },
  { src: "tf", col: "unload_fx_rate" },
  { src: "tc", col: "annex_original_status" },
  { src: "tc", col: "cmr_original_status" },
  { src: "tc", col: "original_docs_status" },
  { src: "t", col: "priority" },
  { src: "t", col: "check_overrides" },
  { src: "tc", col: "annex_copy_status" },
  { raw: `
    (EXISTS ( SELECT 1
           FROM "public"."email_sends" "es"
          WHERE (("es"."transaction_id" = "t"."id") AND ("es"."email_type" = ANY (ARRAY['documents_reminder'::"text", 'documents_scan_reminder'::"text"])) AND ("es"."sent_at" IS NOT NULL)))) AS "documents_reminder_ever_sent"` },
  { src: "tc", col: "copies_missing_status" },
  { src: "tc", col: "copies_rejected_status" },
  { src: "tc", col: "cmr_copy_approved_check" },
  { src: "tc", col: "weight_ticket_copy_approved_check" },
  { src: "t", col: "documents_reminder_blocked" },
  // "Data załadunku" sort key (migration 20261020151108_transactions_effective_load_date).
  { raw: `
    COALESCE("t"."load_date", "t"."pickup_date_from") AS "effective_load_date"` },
];

export const WITH_CHECKS_FROM_SQL = `   FROM (("public"."transactions" "t"
     LEFT JOIN "public"."transaction_checks" "tc" ON (("tc"."transaction_id" = "t"."id")))
     LEFT JOIN "public"."transaction_financials" "tf" ON (("tf"."transaction_id" = "t"."id")))`;
